import { PlainTooltip } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import FiltersViewSubmitButton from "app/pages/sales-intelligence/sub-modules/signals/components/buttons/FiltersViewSubmitButton/FiltersViewSubmitButton";
import { StyledFilterLabelLowercase } from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import useSignalFiltersViewResolver from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalFiltersViewResolver";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type SignalUpdateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalUpdateDTO";
import type { SubmitRendererProps } from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import type { ReactElement } from "react";
import { createElement, useMemo } from "react";

type Props<F extends SignalFilters> = {
    isSubmitInProgress: boolean;
    uiSignal: Readonly<UISignal<F>>;
    onUpdateSubmit(dto: Readonly<SignalUpdateDTO<F>>): void;
};

const SignalsUpdateView = <F extends SignalFilters>(props: Props<F>) => {
    const { isSubmitInProgress, uiSignal, onUpdateSubmit } = props;

    const viewResolver = useSignalFiltersViewResolver();

    const FiltersView = useMemo(() => {
        return viewResolver.getFiltersViewWithValidation(uiSignal.signalType);
    }, [uiSignal.signalType, viewResolver]);

    function handleUpdateSubmit(filters: Readonly<F>) {
        onUpdateSubmit(signalsService.getSignalUpdateDTO(uiSignal, filters));
    }

    function renderTemplateView(): ReactElement {
        return (
            <I18n
                translatable={uiSignal.template.name}
                element={StyledFilterLabelLowercase}
            />
        );
    }

    function renderSubmitView({
        isSignalValid = false,
        isDisabled = false,
        onSubmit,
    }: SubmitRendererProps): ReactElement {
        return (
            <PlainTooltip
                enabled={isSignalValid ? isDisabled : null}
                tooltipContent={translate("si.signals.button.update.disabled.tooltip")}
            >
                <div>
                    <FiltersViewSubmitButton
                        isDisabled={isDisabled || !isSignalValid}
                        isSubmitInProgress={isSubmitInProgress}
                        text={{ key: "si.signals.button.update.text" }}
                        onClick={onSubmit}
                    />
                </div>
            </PlainTooltip>
        );
    }

    if (typeof FiltersView === "undefined") {
        // TODO: Possibly worth throwing an error and catching it in ErrorBoundary
        console.error("SignalUpdateView: unable to find filters view");

        return null;
    }

    // FiltersView is resolved dynamically per signal type and wrapped by an HOC on every
    // resolution, so it can't be expressed as a static JSX tag; the useMemo above keeps it stable.
    return createElement(FiltersView, {
        isActive: true,
        filters: uiSignal.filters,
        signalSubType: uiSignal.signalSubType,
        signalAlias: uiSignal.template.alias,
        signalTemplateHint: uiSignal.template.templateHint,
        renderTemplateView,
        renderSubmit: renderSubmitView,
        onFiltersSubmit: handleUpdateSubmit,
    });
};

export default SignalsUpdateView;
